import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Divider
} from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PlanoAcaoResultadoConsulta from '../../types/negocio/PlanoAcaoResultadoConsulta';
import BasicModal from './BasicModal';
import { ESTRATEGIA_POR_NOME, type EstrategiaName } from '../../types/constants/estrategias';

interface Props {
    open: boolean;
    onClose: () => void;
    plano: PlanoAcaoResultadoConsulta;
    onEdit: () => void;
}

const PlanoDeAcaoDetalhesModal: React.FC<Props> = ({
    open,
    onClose,
    plano,
    onEdit,
}) => {
    const prazoFormatado = plano.prazoConclusao 
        ? format(new Date(plano.prazoConclusao), 'dd/MM/yyyy', { locale: ptBR }) 
        : '-';

    const dataInicioFormatada = plano.dataInicio 
        ? format(new Date(plano.dataInicio), 'dd/MM/yyyy', { locale: ptBR }) 
        : '-';

    const getStatusColor = (status: string) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('finalizada')) return 'success';
        if (statusLower.includes('em andamento')) return 'info';
        if (statusLower.includes('pendente') || statusLower.includes('cancelada')) return 'error';
        if (statusLower.includes('planejada')) return 'default';
        return 'warning';
    };

    const getPrioridadeColor = (prioridade: string): "error" | "warning" | "info" | "success" | "default" => {
        switch (prioridade) {
            case 'Muito alta': return 'error';
            case 'Alta': return 'warning';
            case 'Média': return 'info';
            case 'Baixa': return 'success';
            case 'Muito baixa': return 'default';
            default: return 'default';
        }
    };

    const estrategiaInfo = ESTRATEGIA_POR_NOME[plano.estrategia as EstrategiaName];

    return (
        <BasicModal
            open={open}
            onClose={onClose}
            title="Detalhes do Plano de Ação"
            sizeContent="large"
            onEdit={onEdit}
            tela="plano-de-acao"
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: '700px' }}>
                
                {/* Estratégia */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Estratégia
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip 
                            label={plano.estrategia || 'Sem Estratégia'} 
                            color={plano.estrategia ? "primary" : "default"} 
                            size="small"
                        />
                        {estrategiaInfo ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {estrategiaInfo.descricao}
                            </Typography>
                        ) : !plano.estrategia && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Faça o cruzamento de Fator Interno com Fator Externo para gerar a classificação da sua estratégia dentro do plano de ação.
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Divider />

                {/* Análise SWOT */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            Fator Externo
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {plano.fatorExternoDescricao || '-'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            Fator Interno
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {plano.fatorInternoDescricao || '-'}
                        </Typography>
                    </Box>
                </Box>

                <Divider />

                {/* Meta Global */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Meta Global
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {plano.area && plano.metaGlobalDescricao 
                            ? `${plano.area}: ${plano.metaGlobalDescricao}` 
                            : plano.metaGlobalDescricao || '-'}
                    </Typography>
                </Box>

                <Divider />

                {/* Ação */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Ação
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 'medium' }}>
                        {plano.acao}
                    </Typography>
                </Box>

                <Divider />

                {/* Informações de Execução */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            Responsável
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {plano.responsavelNome || '-'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            Data Início
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {dataInicioFormatada}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            Prazo
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {prazoFormatado}
                        </Typography>
                    </Box>
                </Box>

                {/* Status e Prioridade */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                            <Chip 
                                label={plano.status || 'Sem status'} 
                                color={getStatusColor(plano.status || '')}
                                size="small"
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                            Prioridade
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                            <Chip 
                                label={plano.prioridade || 'Sem prioridade'} 
                                color={getPrioridadeColor(plano.prioridade || '')}
                                size="small"
                                variant={plano.prioridade ? "filled" : "outlined"}
                            />
                        </Box>
                    </Box>
                </Box>

                {plano.arquivado && (
                    <Box sx={{ 
                        bgcolor: 'warning.light', 
                        color: 'warning.dark', 
                        p: 2, 
                        borderRadius: 1,
                        textAlign: 'center'
                    }}>
                        <Typography variant="body2" fontWeight="bold">
                            ⚠️ Este plano está arquivado
                        </Typography>
                    </Box>
                )}
            </Box>
        </BasicModal>
    );
};

export default PlanoDeAcaoDetalhesModal;
